import { type FC, useState } from 'react';

import InlineEditableField from '@zen/Components/InlineEditableField';
import LabelledValue from '@zen/Components/LabelledValue';
import ValueWithDefault from '@zen/Components/ValueWithDefault';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performMutation } from '@zen/utils/performMutation';

import { useUpdateCargoDescriptionMutation } from './graphql';

interface Props {
  canUpdateCargoDescription: boolean;
  cargoDescription: string;
  cargoId: string;
}

const CargoDescriptionInlineField: FC<Props> = ({ canUpdateCargoDescription, cargoDescription, cargoId }) => {
  const [cargoDescriptionValue, setCargoDescriptionValue] = useState<string>(cargoDescription);

  const { addSuccess, addError } = useNotification();

  const [updateCargoDescription] = useUpdateCargoDescriptionMutation();

  const label: string = 'Description';

  const handleChange = (value: string): Promise<IOkOrErrorResult> => {
    if (!value || value.trim().length === 0) {
      addError(`${label} must not be blank`);
      throw Error();
    }

    return performMutation({
      mutationFn: () =>
        updateCargoDescription({
          variables: {
            input: {
              cargoDescription: value,
              cargoId
            }
          }
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess(`${label} updated.`);
        setCargoDescriptionValue(value);
      }
    });
  };

  if (!canUpdateCargoDescription) {
    return (
      <LabelledValue label={label} variant="default">
        <ValueWithDefault value={cargoDescriptionValue} />
      </LabelledValue>
    );
  }

  return (
    // This feels a bit hacky to get this to display correctly due to the ml-3 on the inline editable field label and input.
    <div className="-ml-3">
      <InlineEditableField
        copyable={false}
        label={label}
        name="cargo-description"
        onUpdate={handleChange}
        placeholder="Enter cargo description"
        value={cargoDescriptionValue}
      />
    </div>
  );
};

export default CargoDescriptionInlineField;
